import React, { useState } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ShieldCheck, LifeBuoy, Sparkles, Bot, Mail, Info } from 'lucide-react';
import { useUserFacingError } from '../hooks/useUserFacingError';
import { LEGAL_LINKS } from '../constants/legalLinks';
import {
  PROFILE_AGREEMENTS,
  PROFILE_PERMISSIONS,
  CONSENT_MIRROR_FIELD,
  isConsentGranted,
  effectiveConsent,
  buildConsentRecord,
} from '../constants/onboardingConsents';
import ConsentToggle from './ConsentToggle';

// Consents & permissions card (handoff 11). Shows the sign-up agreements
// read-only (date + version), plus the optional permissions the user controls
// as immediate-save toggles. Each toggle appends an append-only consent record
// to users/{uid}.consents[] and writes the O(1) mirror field. State is read
// from the live `profile` doc passed down from UserProfile's onSnapshot, so a
// failed write reverts automatically once the snapshot re-resolves.

const PERMISSION_ICON = {
  marketing_emails: Mail,
  support_access: LifeBuoy,
  platform_improvement: Sparkles,
  ai_assist_disclosure: Bot,
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

export default function ConsentsCard({ profile }) {
  const consents = profile?.consents || [];
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const { logError } = useUserFacingError();

  async function setConsent(def, granted) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setSavingId(def.id);
    const record = buildConsentRecord(def, {
      uid,
      granted,
      // snapshot the meaningful wording (description when present) so the record
      // is self-describing alongside the version -> registry mapping.
      wording: def.description || def.label,
      nowIso: new Date().toISOString(),
      source: 'profile_consents',
    });
    try {
      await updateDoc(doc(db, 'users', uid), {
        consents: arrayUnion(record),
        [CONSENT_MIRROR_FIELD[def.id]]: granted,
      });
      // profile re-hydrates via the parent onSnapshot; show a brief confirmation.
      setSavedId(def.id);
      setTimeout(() => setSavedId((s) => (s === def.id ? null : s)), 1600);
    } catch (e) {
      logError('Failed to save consent', e); // UI reverts once the snapshot re-resolves.
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="border-t border-slate-100 pt-6">
      <h3 className="font-bold text-slate-900 mb-1">Consents &amp; permissions</h3>
      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        Everything you have agreed to, in one place. The optional permissions are yours to change
        at any time. Changes save immediately and are recorded with a timestamp.
      </p>

      {/* Agreements (read-only) */}
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Your agreements</p>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 mb-5">
        {PROFILE_AGREEMENTS.map(({ def, title, note, verb, link }) => {
          const rec = effectiveConsent(consents, def.id);
          const when = rec ? `${verb} ${fmtDate(rec.granted_at)} · v${rec.version}` : `${verb} at sign-up`;
          return (
            <div key={def.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">{title}</p>
                  <p className="text-[11px] text-slate-400">{note || when}</p>
                </div>
              </div>
              {link
                ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#3d9dad] underline underline-offset-2 hover:text-[#065354] whitespace-nowrap shrink-0">View</a>
                : <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{when}</span>}
            </div>
          );
        })}
      </div>

      {/* Permissions you control (immediate-save toggles) */}
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Permissions you control</p>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
        {PROFILE_PERMISSIONS.map((def) => {
          const Icon = PERMISSION_ICON[def.id] || Info;
          const checked = isConsentGranted(consents, def.id);
          return (
            <div key={def.id} className="flex items-start justify-between gap-4 px-4 py-3.5">
              <div className="flex items-start gap-2.5">
                <Icon size={16} className="text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">{def.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{def.description}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0 pt-0.5">
                <ConsentToggle
                  checked={checked}
                  busy={savingId === def.id}
                  label={def.label}
                  onChange={(next) => setConsent(def, next)}
                />
                <span className={'text-[10px] font-semibold text-emerald-600 transition-opacity ' + (savedId === def.id ? 'opacity-100' : 'opacity-0')}>Saved</span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5 leading-relaxed">
        <Info size={13} className="shrink-0 mt-0.5" />
        <span>Each choice is recorded as a dated, versioned consent record. See the{' '}
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="text-[#3d9dad] underline underline-offset-2 hover:text-[#065354]">Privacy Policy</a>{' '}
          for how each permission is used.</span>
      </p>
    </div>
  );
}
